export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount); // Assuming amount is in cents
};

export const formatDocument = (document: string): string => {
  // Remove non-numeric characters
  const cleanDoc = document.replace(/\D/g, '');
  
  if (cleanDoc.length === 11) {
    // CPF format: 000.000.000-00
    return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (cleanDoc.length === 14) {
    // CNPJ format: 00.000.000/0000-00
    return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  
  return document;
};

export const validateCPF = (cpf: string): boolean => {
  const cleanCPF = cpf.replace(/\D/g, '');
  
  if (cleanCPF.length !== 11) return false;
  
  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
  
  // Validate check digits
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF[i]) * (10 - i);
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(cleanCPF[9]) !== digit1) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF[i]) * (11 - i);
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  return parseInt(cleanCPF[10]) === digit2;
};

export const getPaymentStatusLabel = (paymentStatusId: number): string => {
  switch (paymentStatusId) {
    case 1: return 'Pendente';
    case 2: return 'Pago';
    case 3: return 'Falhou';
    case 4: return 'Pago Offline';
    case 5: return 'Cancelado';
    case 6: return 'Reembolsado';
    default: return 'Desconhecido';
  }
};

export const getPaymentStatusColor = (paymentStatusId: number): string => {
  switch (paymentStatusId) {
    case 1: return 'text-yellow-600 bg-yellow-100'; // pending
    case 2: return 'text-green-600 bg-green-100';   // paid
    case 3: return 'text-red-600 bg-red-100';       // failed
    case 4: return 'text-blue-600 bg-blue-100';     // paid_offline
    case 5: return 'text-gray-600 bg-gray-100';     // cancelled
    case 6: return 'text-purple-600 bg-purple-100'; // refunded
    default: return 'text-gray-600 bg-gray-100';
  }
};

export const isPaymentPaid = (paymentStatusId: number): boolean => {
  return paymentStatusId === 2 || paymentStatusId === 4; // PAID or PAID_OFFLINE
};

export const calculateAge = (birthDate: string): number => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

export const isMinor = (birthDate: string): boolean => {
  return calculateAge(birthDate) < 18;
};

export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};
